export type SessionPackage = {
  id: string;
  label: string;
  description: string;
  price: number;
  perSession: number;
  /** Optional highlight pill shown on the booking option (e.g. a limited offer) */
  badge?: string;
  /** Optional "what's included" note shown under the option and in the summary */
  includes?: string;
  /**
   * Optional "See what's inside" target for the `includes` note. Only set where
   * there is genuinely something to look at — this used to be a hardcoded link
   * to the Express Detox Pack rendered under every package that had an
   * `includes` note, which sent 2-Sessions-in-a-Week buyers to a detox bundle.
   */
  includesHref?: string;
  /**
   * Days from the first session within which the remaining sessions must be
   * used. Omitted means the block never lapses, which is how block5/block10
   * have always worked. Enforced server-side — see `packageExpiryDate`.
   */
  validForDays?: number;
};

export const PACKAGES: SessionPackage[] = [
  {
    id: "detox-combo",
    label: "Express Mini Detox + 2 Sessions",
    description: "Express Mini Detox supplement pack + 2 × 45-minute Body Ballancer sessions",
    price: 120,
    perSession: 35,
    badge: "Special Offer",
    includes:
      "Includes the Express Mini Detox pack, handed to you in person at your first session at Stocks House — no shipping needed.",
    includesHref: "/shop/bundles/express-detox-pack",
  },
  {
    id: "single",
    label: "Single Session",
    description: "One 45-minute Body Ballancer session",
    price: 45,
    perSession: 45,
  },
  {
    id: "block2-week",
    label: "2 Sessions in a Week",
    description: "2 × 45-minute sessions, both taken within 7 days (£35 per session)",
    price: 70,
    perSession: 35,
    validForDays: 7,
    includes:
      "Both sessions must be booked within 7 days of the first — back-to-back sessions are what make the difference.",
  },
  {
    id: "block5",
    label: "Block of 5",
    description: "5 × 45-minute sessions (£40 per session)",
    price: 200,
    perSession: 40,
  },
  {
    id: "block10",
    label: "Block of 10",
    description: "10 × 45-minute sessions (£35 per session)",
    price: 350,
    perSession: 35,
  },
];

/**
 * Internal-only packages — resolvable server-side (via PACKAGE_MAP) but NOT
 * rendered on the public booking widget, which iterates PACKAGES directly.
 * `staff` is the £10 staff rate booked from the Stocks House admin dashboard.
 */
export const INTERNAL_PACKAGES: SessionPackage[] = [
  {
    id: "staff",
    label: "Staff Session",
    description: "One 45-minute Body Ballancer session (staff rate)",
    price: 10,
    perSession: 10,
  },
];

export const PACKAGE_MAP = new Map<string, SessionPackage>(
  [...PACKAGES, ...INTERNAL_PACKAGES].map((p) => [p.id, p])
);

/** Calendar slot duration in minutes — 45-min treatment + 15-min buffer */
export const SLOT_DURATION_MINS = parseInt(
  process.env.BALLANCER_SLOT_DURATION ?? "60",
  10
);

/** Total sessions included in each package */
export const PACKAGE_SESSIONS: Record<string, number> = {
  single: 1,
  staff: 1,
  "block2-week": 2,
  block5: 5,
  block10: 10,
  "detox-combo": 2,
};

/**
 * Last date the remaining sessions on a block may be booked for, or null when
 * the package never lapses.
 *
 * Inclusive of the final day: a `validForDays: 7` block bought for a Monday is
 * bookable up to and including the Monday after. The generous reading is
 * deliberate — someone counting days on their fingers should not be refused on
 * an off-by-one.
 *
 * Dates are compared as plain "YYYY-MM-DD" strings throughout, so this never
 * has to reason about timezones or BST.
 */
export function packageExpiryDate(packageId: string, firstBookingDate: string): string | null {
  const days = PACKAGE_MAP.get(packageId)?.validForDays;
  if (!days) return null;
  const d = new Date(`${firstBookingDate}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return null;
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}
